import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';

interface BalancesReportProps {
  entityId: string;
  periodId: string;
  viewMode: 'current' | 'daily';
  setViewMode: (mode: 'current' | 'daily') => void;
  days: number;
  setDays: (days: number) => void;
}

export default function BalancesReport({ entityId, periodId, viewMode, setViewMode, days, setDays }: BalancesReportProps) {
  const { t } = useTranslation('reports');
  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data: currentData, isLoading: currentLoading } = useQuery({
    queryKey: ['report-balances', entityId],
    queryFn: () => api.reports.balances(entityId),
    enabled: viewMode === 'current',
  });

  const { data: dailyData, isLoading: dailyLoading } = useQuery({
    queryKey: ['report-balances-daily', entityId, periodId, days],
    queryFn: async () => {
      const res = await fetch(`/api/reports/balances?daily=true&days=${days}${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'daily',
  });

  const isLoading = viewMode === 'current' ? currentLoading : dailyLoading;

  if (isLoading) return <LoadingState message={t('balances.loading')} />;

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="controls-row">
          <div className="view-toggle">
            <button
              className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('daily')}
            >
              {t('views.daily')}
            </button>
            <button
              className={`btn ${viewMode === 'current' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('current')}
            >
              {t('views.current')}
            </button>
          </div>
          {viewMode === 'daily' && (
            <select className="form-input" value={days} onChange={(e) => setDays(parseInt(e.target.value))} style={{width: 'auto'}}>
              <option value="7">7 {t('balances.days')}</option>
              <option value="14">14 {t('balances.days')}</option>
              <option value="30">30 {t('balances.days')}</option>
              <option value="90">90 {t('balances.days')}</option>
            </select>
          )}
        </div>
      </div>

      {viewMode === 'daily' ? (
        <DailyBalancesTable data={dailyData} />
      ) : (
        <CurrentBalances data={currentData} />
      )}

      <style>{`
        .controls-row { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
        .view-toggle { display: flex; gap: 8px; }
      `}</style>
    </div>
  );
}

function DailyBalancesTable({ data }: { data: any }) {
  const { t } = useTranslation('reports');

  if (!data?.dates?.length || !data?.byType) {
    return (
      <div className="card">
        <EmptyState title={t('balances.empty.title')} description={t('balances.empty.description')} />
      </div>
    );
  }

  // Format date for column header (e.g., "15 Dec" or "15 дек")
  const formatDateHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${day} ${month}`;
  };

  // Type labels and colors
  const typeConfig: Record<string, { label: string; color: string; darkColor: string }> = {
    cash: { label: t('balances.cash'), color: '#c8e6c9', darkColor: '#2e7d32' },
    bank: { label: t('balances.bank'), color: '#bbdefb', darkColor: '#1565c0' },
    crypto: { label: t('balances.crypto'), color: '#e1bee7', darkColor: '#7b1fa2' },
    payment_system: { label: t('balances.paymentSystems'), color: '#ffe0b2', darkColor: '#ef6c00' },
  };

  // Calculate totals by currency across all dates
  const calculateTotals = () => {
    const totals: Record<string, Record<string, number>> = {};

    Object.values(data.byType).forEach((wallets: any) => {
      (wallets as any[]).forEach((w) => {
        if (!totals[w.currency]) {
          totals[w.currency] = {};
          data.dates.forEach((date: string) => {
            totals[w.currency][date] = 0;
          });
        }
        data.dates.forEach((date: string) => {
          totals[w.currency][date] += parseFloat(w.balances[date] || '0');
        });
      });
    });

    return totals;
  };

  const totals = calculateTotals();
  const currencies = Object.keys(totals).sort();

  return (
    <div className="card">
      <div className="balances-table-wrapper">
        <table className="balances-table">
          <thead>
            <tr>
              <th className="header-label">{t('balances.title')}</th>
              <th className="header-currency">{t('columns.currency')}</th>
              {data.dates.map((date: string) => (
                <th key={date} className="header-date">{formatDateHeader(date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.byType).map(([type, wallets]: [string, any]) => {
              const config = typeConfig[type] || { label: type, color: '#e0e0e0', darkColor: '#616161' };
              return (
                <>
                  <tr key={type} className="type-header" style={{ '--type-color': config.color, '--type-dark-color': config.darkColor } as React.CSSProperties}>
                    <td colSpan={2 + data.dates.length}>
                      {config.label}
                    </td>
                  </tr>
                  {(wallets as any[]).map((w) => (
                    <tr key={w.walletId}>
                      <td className="cell-wallet">{w.walletName}</td>
                      <td className="cell-currency">{w.currency}</td>
                      {data.dates.map((date: string) => {
                        const value = parseFloat(w.balances[date] || '0');
                        return (
                          <td key={date} className={`cell-value ${value < 0 ? 'text-error' : ''}`}>
                            {formatAmount(w.balances[date] || '0', w.currency)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })}

            {/* Totals Section */}
            <tr className="totals-header">
              <td colSpan={2 + data.dates.length}>{t('balances.totals')}</td>
            </tr>
            {currencies.map((currency) => (
              <tr key={`total-${currency}`} className="totals-row">
                <td className="cell-wallet">{t('cashflow.total')}</td>
                <td className="cell-currency">{currency}</td>
                {data.dates.map((date: string) => {
                  const value = totals[currency][date];
                  return (
                    <td key={date} className={`cell-value cell-total ${value < 0 ? 'text-error' : ''}`}>
                      {formatAmount(value.toString(), currency)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .balances-table-wrapper {
          overflow-x: auto;
          overflow-y: visible;
        }

        .balances-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 12px;
          white-space: nowrap;
        }

        .balances-table th,
        .balances-table td {
          border: 1px solid var(--color-border);
          padding: 6px 10px;
        }

        .balances-table thead th {
          background: var(--color-table-header);
          font-weight: 600;
          text-align: center;
        }

        .header-label {
          text-align: left !important;
          min-width: 200px;
        }

        .header-currency {
          min-width: 70px;
        }

        .header-date {
          min-width: 80px;
        }

        .type-header {
          background: var(--type-color) !important;
          font-weight: 600;
        }

        .type-header td {
          padding: 8px 10px;
        }

        .cell-wallet {
          text-align: left;
          padding-left: 16px !important;
        }

        .cell-currency {
          text-align: center;
          font-weight: 500;
        }

        .cell-value {
          text-align: right;
          font-family: monospace;
        }

        .totals-header {
          background: #ffcdd2 !important;
          font-weight: 600;
        }

        .totals-header td {
          padding: 8px 10px;
        }

        .totals-row {
          background: #ffebee;
        }

        .cell-total {
          font-weight: 600;
        }

        .text-error {
          color: var(--color-error);
        }

        /* Alternating rows within each section */
        .balances-table tbody tr:not(.type-header):not(.totals-header):not(.totals-row):nth-child(even) {
          background: var(--color-table-row-alt);
        }

        /* Dark theme adjustments */
        :root[data-theme="dark"] .type-header {
          background: var(--type-dark-color) !important;
          color: white;
        }

        :root[data-theme="dark"] .totals-header {
          background: #c62828 !important;
          color: white;
        }

        :root[data-theme="dark"] .totals-row {
          background: rgba(198, 40, 40, 0.2);
        }
      `}</style>
    </div>
  );
}

function CurrentBalances({ data }: { data: any }) {
  const { t } = useTranslation('reports');

  if (!data?.byType || Object.keys(data.byType).length === 0) {
    return (
      <div className="card">
        <EmptyState title={t('balances.noWallets.title')} description={t('balances.noWallets.description')} />
      </div>
    );
  }

  const typeLabels: Record<string, string> = {
    cash: t('balances.cash'),
    bank: t('balances.bank'),
    crypto: t('balances.crypto'),
    payment_system: t('balances.paymentSystems'),
  };

  return (
    <>
      {data?.totals?.length > 0 && (
        <div className="card mb-4">
          <h3 className="card-title mb-4">{t('cashflow.total')}</h3>
          <div className="totals-grid">
            {data.totals.map((tot: any) => (
              <div key={tot.currency} className="total-card">
                <span className="total-currency">{tot.currency}</span>
                <span className="total-amount">{formatAmount(tot.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.entries(data.byType).map(([type, wallets]: [string, any]) => (
        <div key={type} className="card mb-4">
          <h3 className="card-title mb-4">{typeLabels[type] || type}</h3>
          <table className="table">
            <thead>
              <tr>
                <th>{t('columns.wallet')}</th>
                <th>{t('columns.currency')}</th>
                <th className="text-right">{t('columns.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {(wallets as any[]).map((w) => (
                <tr key={w.walletId}>
                  <td>{w.walletName}</td>
                  <td>{w.currency}</td>
                  <td className={`text-right font-mono ${parseFloat(w.balance) < 0 ? 'text-error' : ''}`}>
                    {formatAmount(w.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <style>{`
        .totals-grid { display: flex; gap: 16px; flex-wrap: wrap; }
        .total-card { display: flex; flex-direction: column; gap: 4px; padding: 16px 24px; background: var(--color-gray-50); border-radius: var(--radius); }
        .total-currency { font-size: 14px; color: var(--color-gray-500); }
        .total-amount { font-size: 24px; font-weight: 600; font-family: monospace; }
        .font-mono { font-family: monospace; }
      `}</style>
    </>
  );
}
